import { sendGet } from '../../hooks/use-fetch'
import DataTable from '../../Components/Table/DataTable'
import { type PaginationProps, usePagination } from '../../hooks/use-pagination'
import { AggregationType, Column, ColumnType, type RowData } from '../../Components/Table/DataTable/DataTable.model'
import { useEffect, useState } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { DatePickerWithLabel } from '../../Components/DatePicker'
import { Autocomplete } from '../../Components/AutoComplete'
import { type lookup } from '../../core/types'
import { useAccountId } from '../../ContextProviders/CurrentAccount'

const providerNameColumn = new Column('Name', 'Provider Name', ColumnType.TEXT, 1, undefined, 'Totals', AggregationType.STATICTEXT)
const referredColumn = new Column('Referred', 'Referred', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const processingColumn = new Column('Processing', 'Processing', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const waitlistedColumn = new Column('WaitListed', 'WaitListed', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const enrolledColumn = new Column('Enrolled', 'Enrolled', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedPartGSRP400 = new Column('StartedPartGSRP400', '0-400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedPartGSRP401 = new Column('StartedPartGSRP401', '> 400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)

const StartedGSRP400 = new Column('StartedGSRP400', '0-400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedGSRP401 = new Column('StartedGSRP401', '> 400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)

const StartedExtendedGSRP400 = new Column('StartedExtendedGSRP400', '0-400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedExtendedGSRP401 = new Column('StartedExtendedGSRP401', '> 400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)

const StartedPartBlend400 = new Column('StartedPartBlend400', '0-400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedPartBlend401 = new Column('StartedPartBlend401', '> 400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)

const StartedBlend400 = new Column('StartedBlend400', '0-400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedBlend401 = new Column('StartedBlend401', '> 400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)

const StartedExtendedBlend400 = new Column('StartedExtendedBlend400', '0-400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const StartedExtendedBlend401 = new Column('StartedExtendedBlend401', '> 400%', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)

const totalColumn = new Column('TotalActive', 'Total Active', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const totalExited = new Column('TotalExited', 'Total Exited', ColumnType.TEXT, 1, undefined, '', AggregationType.SUM)
const nonBreakingSpace = '\u00A0'
const gsrpPartDay = new Column('', `GSRP Started - Part${nonBreakingSpace}Day`, ColumnType.TEXT, 2, [StartedPartGSRP400, StartedPartGSRP401])
const gsrpSchoolDay = new Column('', `GSRP Started - School${nonBreakingSpace}Day`, ColumnType.TEXT, 2, [StartedGSRP400, StartedGSRP401])
const gsrpExtendedDay = new Column('', `GSRP Started - Extended${nonBreakingSpace}Day`, ColumnType.TEXT, 2, [StartedExtendedGSRP400, StartedExtendedGSRP401])
const hsPartDay = new Column('', `HS Blend Started - Part${nonBreakingSpace}Day`, ColumnType.TEXT, 2, [StartedPartBlend400, StartedPartBlend401])
const hsSchoolDay = new Column('', `HS Blend Started - School${nonBreakingSpace}Day`, ColumnType.TEXT, 2, [StartedBlend400, StartedBlend401])
const hsExtendedDay = new Column('', `HS Blend Started - Extended${nonBreakingSpace}Day`, ColumnType.TEXT, 2, [StartedExtendedBlend400, StartedExtendedBlend401])
const columns = [providerNameColumn, referredColumn, processingColumn, waitlistedColumn, enrolledColumn,
  gsrpPartDay,
  gsrpSchoolDay,
  gsrpExtendedDay,
  hsPartDay,
  hsSchoolDay,
  hsExtendedDay,
  totalColumn,
  totalExited
]

export function MiLeapReport (): JSX.Element {
  const [data, setData] = useState<RowData[]>([])
  const [pageLoaded, setPageLoaded] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const fetchData = async (): Promise<void> => {
    setFetchingData(true)
    const querySting: string[] = []
    if (selectedYear != null) { querySting.push(`schoolYearId=${selectedYear}`) }
    if (selectedStartDate != null) { querySting.push(`startDate=${selectedStartDate.toDateString()}`) }
    if (selectedEndDate != null) { querySting.push(`endDate=${selectedEndDate.toDateString()}`) }
    if (selectedISDProviderId != null) { querySting.push(`isdServiceProviderId=${selectedISDProviderId}`) }
    const str = querySting.length > 0 ? querySting.join('&') : ''
    const reportData = await sendGet(`/NewEnrollment/MiLeapReport?${str}`, {})
    setData(reportData.response)
    setFetchingData(false)
  }
  const [allProviders, setAllProviders] = useState<lookup[]>([])
  const [timeSpans, setTimeSpans] = useState<lookup[]>([])
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null)
  const [selectedISDProviderId, setSelectedProviderId] = useState<number | null>(null)
  const acct = useAccountId()?.id
  const paginationProps: PaginationProps<RowData> = {
    rows: data,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)
  useEffect(() => {
    const initialLoad = async (): Promise<void> => {
      const providerList = await sendGet(`/ISDProviderManagement/GetAll?isdid=${acct ?? 0}`, {})
      setAllProviders(providerList.response)
      const timeSpanList = await sendGet('/DateRange/GetAll', {})
      setTimeSpans(timeSpanList.response)
      setPageLoaded(true)
    }
    void initialLoad()
  }, [])

  const mapKeys = (keys: string[]): string[] => {
    const newKeys = keys.map(k => {
      const isLessThan400 = k.endsWith('400')
      const isGSRP = k.includes('GSRP')
      const isPart = k.includes('Part')
      const isExtended = k.includes('Extended')
      switch (k.startsWith('Started')) {
        case true: {
          let str = isGSRP ? 'GSRP ' : 'HS Blend '
          str += isPart ? 'Part-Day ' : ''
          str += !isPart && !isExtended ? 'School-Day ' : ''
          str += isExtended ? 'Extended-Day ' : ''
          str += isLessThan400 ? '0-400%' : '> 400%'
          return str
        }
        default:
          return k
      }
    })

    return newKeys
  }
  const download = (): void => {
    if (data.length === 0) return
    const { id, ...rest } = data[0]
    let keys = Object.keys(rest)
    keys = mapKeys(keys)
    const joinedKeys = keys.join()
    const values = data
      .map((e) => {
        const { id, ...rest } = e
        return Object.values(rest)
          .map((v) => {
            let str = v?.toString()
            if (str !== undefined) {
              str = str.replaceAll(',', ';')
              return str
            }
            return v
          })
          .join()
      })
      .join('\n')
    const fileData = [
      ['data:text/csv;charset=utf-8', joinedKeys].join(),
      values
    ].join('\n')
    const encodedUri = encodeURI(fileData)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    const text = (selectedYear ?? '') !== '' ? timeSpans.find(t => t.id === selectedYear)?.name.replaceAll(' ', '_') ?? '' : 'All'
    link.setAttribute('download', `MiLeapReport${text}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  if (!pageLoaded) return <CircularProgress />
  return <>
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
      <Box minWidth={'200px'} >
        <Autocomplete
          options={timeSpans}
          value={selectedYear ?? ''}
          onChange={(e, value) => {
            const newValue = parseInt(value?.id.toString() ?? '')
            setSelectedYear(newValue)
          } }
          name='schoolYear'
          label='School Year'
          width='100%'
        />
      </Box>
      <Box>
        <DatePickerWithLabel
          value={selectedStartDate}
          onChange={(value) => { setSelectedStartDate(value) } }
          name='startDate'
          label='Start Date'
        />
      </Box>
      <Box>
        <DatePickerWithLabel
          value={selectedEndDate}
          onChange={(value) => { setSelectedEndDate(value) } }
          name='endDate'
          label='End Date'
        />
      </Box>
      <Box minWidth={'400px'}>
        <Autocomplete
          width='100%'
          options={allProviders}
          onChange={(e, value) => {
            const val = parseInt(value?.id?.toString() ?? '')
            setSelectedProviderId(val)
          }}
          label={'Provider Name'}
          value={selectedISDProviderId ?? ''}
          name={'providerName'}
        />
      </Box>
      <Box alignContent={'center'}>
        <Button onClick={fetchData} variant='contained'>Run Report</Button>
      </Box>

    </Box>
    {(!pageLoaded) &&
      <CircularProgress />}
    {pageLoaded && (<>
      <DataTable
        page={pagination.page}
        name='miLeapReportTable'
        data-testid='miLeapReportTable'
        hasCheckbox={false}
        columns={columns}
        rows={pagination.internalRows}
        rowClickable={false}
        sortable={false}
        totalRecords={pagination.recordCount}
        onPageChange={pagination.handleChangePage}
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        loading={fetchingData}
        showGrandTotal={!fetchingData && pagination.internalRows.length > 1}
      />
      <Button onClick={download}>download</Button>
    </>
    )}
  </>
}
